import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { ROLES } from "../../../lib/constants";
import toast from "react-hot-toast";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import TelegramLinkForm from "../../../components/user/TelegramLinkForm";
import {
  getMyClientConfig,
  getUserProfile,
  getTelegramWebhookInfo,
  linkTelegramUsername,
  unlinkTelegramAccount,
  rotateClientConfigSecret,
  syncTelegramWebhookFromClientConfig,
  updateMyClientConfig,
  validateClientConfigSection,
  uploadBrandingLogo,
} from "../../../lib/apiClient";
import { getErrorMessage } from "../../../lib/utils";
import { ClientConfigData, ClientConfigRecord, UserProfile, TelegramFormData } from "../../../types";

type ConfigSection = "platform" | "payment" | "kyc" | "branding";

const SECTION_ORDER: ConfigSection[] = [
  "platform",
  "payment",
  "kyc",
  "branding",
];

export default function AdminSettingsPage() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === ROLES.SUPER_ADMIN;
  const [record, setRecord] = useState<ClientConfigRecord | null>(null);
  const [form, setForm] = useState<ClientConfigData>({});
  const [activeTab, setActiveTab] = useState<ConfigSection>("platform");

  // Telegram personal account state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isTelegramLoading, setIsTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState<string | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState("{}");
  const [secretPresence, setSecretPresence] = useState<Record<string, boolean>>({});
  const [secretReveal, setSecretReveal] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<Record<ConfigSection, boolean>>({
    platform: false,
    payment: false,
    kyc: false,
    branding: false,
  });
  const [isValidating, setIsValidating] = useState<Record<ConfigSection, boolean>>({
    platform: false,
    payment: false,
    kyc: false,
    branding: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSyncingTelegramWebhook, setIsSyncingTelegramWebhook] = useState(false);
  const [webhookConnected, setWebhookConnected] = useState<boolean | null>(null);

  const hydrateForm = useCallback((data: ClientConfigRecord) => {
    setRecord(data);

    const cfg = data.config || {};
    const hydrated: ClientConfigData = {
      app: {
        frontendUrl: cfg.app?.frontendUrl || "",
        publicApiUrl: cfg.app?.publicApiUrl || "",
      },
      platform: {
        telegram: {
          botToken: "",
          botUsername: cfg.platform?.telegram?.botUsername || "",
        },
        discord: {
          botToken: "",
          publicKey: cfg.platform?.discord?.publicKey || "",
          clientId: cfg.platform?.discord?.clientId || "",
          clientSecret: "",
          redirectUri: cfg.platform?.discord?.redirectUri || "",
          testUserId: cfg.platform?.discord?.testUserId || "",
        },
        slack: {
          botToken: "",
          appInstallUrl: cfg.platform?.slack?.appInstallUrl || "",
          workspaceUrl: cfg.platform?.slack?.workspaceUrl || "",
        },
        features: {
          telegramEnabled: cfg.platform?.features?.telegramEnabled !== false,
          discordEnabled: cfg.platform?.features?.discordEnabled !== false,
          slackEnabled: cfg.platform?.features?.slackEnabled !== false,
        },
      },
      payment: {
        razorpay: {
          keyIdProd: cfg.payment?.razorpay?.keyIdProd || "",
          keySecretProd: "",
          webhookSecret: "",
        },
      },
      kyc: {
        digio: {
          clientId: cfg.kyc?.digio?.clientId || "",
          clientSecret: "",
        },
      },
      compliance: {
        kycEnabled: cfg.compliance?.kycEnabled === true,
        agreementSigningEnabled: cfg.compliance?.agreementSigningEnabled === true,
      },
      branding: {
        name: cfg.branding?.name || "",
        logoUrl: cfg.branding?.logoUrl || "",
        tagline: cfg.branding?.tagline || "",
      },
    };

    setForm(hydrated);
    setInitialSnapshot(JSON.stringify(hydrated));

    setSecretPresence({
      "platform.telegram.botToken": !!cfg.platform?.telegram?.botToken,
      "platform.discord.botToken": !!cfg.platform?.discord?.botToken,
      "platform.discord.clientSecret": !!cfg.platform?.discord?.clientSecret,
      "platform.slack.botToken": !!cfg.platform?.slack?.botToken,
      "payment.razorpay.webhookSecret": !!cfg.payment?.razorpay?.webhookSecret,
      "payment.razorpay.keySecretProd": !!cfg.payment?.razorpay?.keySecretProd,
      "kyc.digio.clientSecret": !!cfg.kyc?.digio?.clientSecret,
    });
    setSecretReveal({});
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [data, profileData] = await Promise.all([getMyClientConfig(), getUserProfile()]);
      hydrateForm(data);
      setProfile(profileData);
      refreshWebhookStatus();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [hydrateForm]);

  const handleLinkTelegram = async (formData: TelegramFormData) => {
    setIsTelegramLoading(true);
    setTelegramError(null);
    try {
      await linkTelegramUsername(formData);
      const updated = await getUserProfile();
      setProfile(updated);
      toast.success("Telegram account linked.");
    } catch (err) {
      const msg = getErrorMessage(err);
      setTelegramError(msg);
      toast.error(msg);
    } finally {
      setIsTelegramLoading(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    setIsTelegramLoading(true);
    setTelegramError(null);
    try {
      await unlinkTelegramAccount();
      const updated = await getUserProfile();
      setProfile(updated);
      toast.success("Telegram account unlinked.");
    } catch (err) {
      const msg = getErrorMessage(err);
      setTelegramError(msg);
      toast.error(msg);
    } finally {
      setIsTelegramLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const kycEnabled = form.compliance?.kycEnabled === true;

  // Dynamic completion: count filled actionable fields relative to what's enabled.
  const localCompletion = useMemo(() => {
    const checks: Array<() => boolean> = [
      // Telegram (always required)
      () => secretPresence["platform.telegram.botToken"] || !!(form.platform?.telegram?.botToken),
      () => !!(form.platform?.telegram?.botUsername),
      // Payment
      () => !!(form.payment?.razorpay?.keyIdProd),
      () => secretPresence["payment.razorpay.keySecretProd"],
    ];
    // Discord (only when enabled)
    if (form.platform?.features?.discordEnabled) {
      checks.push(
        () => secretPresence["platform.discord.botToken"],
        () => !!(form.platform?.discord?.clientId),
        () => !!(form.platform?.discord?.redirectUri),
      );
    }
    // Slack (only when enabled)
    if (form.platform?.features?.slackEnabled) {
      checks.push(
        () => secretPresence["platform.slack.botToken"],
        () => !!(form.platform?.slack?.workspaceUrl),
      );
    }
    // KYC (only when enabled)
    if (kycEnabled) {
      checks.push(
        () => !!(form.kyc?.digio?.clientId),
        () => secretPresence["kyc.digio.clientSecret"],
      );
    }
    const filled = checks.filter((fn) => fn()).length;
    return checks.length === 0 ? 0 : Math.round((filled / checks.length) * 100);
  }, [form, secretPresence, kycEnabled]);

  const completionText = `${localCompletion}%`;
  const completionColor =
    localCompletion === 100
      ? "bg-functional-success"
      : localCompletion >= 50
      ? "bg-golden-accent"
      : "bg-functional-warning";

  const isDirty = useMemo(() => JSON.stringify(form) !== initialSnapshot, [form, initialSnapshot]);

  // If KYC becomes disabled while KYC tab is active, fall back to platform.
  useEffect(() => {
    if (!kycEnabled && activeTab === "kyc") setActiveTab("platform");
  }, [kycEnabled, activeTab]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "You have unsaved client settings changes.";
    };

    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [isDirty]);

  const updateByPath = (path: string, value: string) => {
    setForm((prev) => {
      const clone = JSON.parse(JSON.stringify(prev || {}));
      const parts = path.split(".");
      let current = clone;

      for (let i = 0; i < parts.length - 1; i += 1) {
        if (!current[parts[i]] || typeof current[parts[i]] !== "object") {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const buildSectionPayload = (section: ConfigSection): Partial<ClientConfigData> => {
    const payload: Partial<ClientConfigData> = {};
    if (section === "platform") {
      // Non-SuperAdmin must not send SuperAdmin-only feature flags
      if (!isSuperAdmin && form.platform?.features) {
        const { telegramEnabled: _t, discordEnabled: _d, slackEnabled: _s, ...otherFeatures } =
          form.platform.features as Record<string, unknown>;
        const features = Object.keys(otherFeatures).length > 0 ? otherFeatures : undefined;
        payload.platform = { ...form.platform, features: features as typeof form.platform.features };
      } else {
        payload.platform = form.platform;
      }
    }
    if (section === "payment") payload.payment = form.payment;
    if (section === "kyc") payload.kyc = form.kyc;
    if (section === "branding") payload.branding = form.branding;
    return JSON.parse(JSON.stringify(payload));
  };

  const onSaveSection = async (section: ConfigSection) => {
    if (section === "platform") {
      const tgToken = form.platform?.telegram?.botToken || "";
      if (tgToken && !/^\d+:[A-Za-z0-9_-]+$/.test(tgToken)) {
        toast.error("Telegram Bot Token format is invalid. It should look like 123456789:ABCDEFghijklmnop (found in BotFather).");
        return;
      }
      const slackToken = form.platform?.slack?.botToken || "";
      if (slackToken && !slackToken.startsWith("xoxb-")) {
        toast.error("Slack Bot Token must start with xoxb-. Copy it from your Slack App → OAuth & Permissions → Bot User OAuth Token.");
        return;
      }
    }
    if (section === "payment") {
      const webhookSecret = form.payment?.razorpay?.webhookSecret || "";
      if (webhookSecret.trim() && /^rzp_/i.test(webhookSecret)) {
        toast.error("Razorpay Webhook Secret looks like a Key ID. Enter the webhook secret from Razorpay Dashboard → Webhooks.");
        return;
      }
      const secret = form.payment?.razorpay?.keySecretProd || "";
      if (/^rzp_/i.test(secret)) {
        toast.error("Live Key Secret looks like a Key ID. Enter the actual API secret from Razorpay Dashboard → Settings → API Keys.");
        return;
      }
    }
    if (section === "kyc") {
      const clientId = (form.kyc?.digio?.clientId || "").trim();
      const clientSecret = (form.kyc?.digio?.clientSecret || "").trim();
      if (clientSecret && clientId && clientSecret === clientId) {
        toast.error("Digio Client Secret appears to be the same as the Client ID. Enter the actual secret from Digio Dashboard → API Credentials.");
        return;
      }
    }
    setIsSaving((prev) => ({ ...prev, [section]: true }));
    try {
      const payload = buildSectionPayload(section);
      const updated = await updateMyClientConfig(payload);
      hydrateForm(updated);
      toast.success(`${section.toUpperCase()} settings saved.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving((prev) => ({ ...prev, [section]: false }));
    }
  };

  const onSaveAll = async () => {
    SECTION_ORDER.forEach((section) => {
      setIsSaving((prev) => ({ ...prev, [section]: true }));
    });

    try {
      const platformForSave = (() => {
        if (!isSuperAdmin && form.platform?.features) {
          const { telegramEnabled: _t, discordEnabled: _d, slackEnabled: _s, ...otherFeatures } =
            form.platform.features as Record<string, unknown>;
          const features = Object.keys(otherFeatures).length > 0 ? otherFeatures : undefined;
          return { ...form.platform, features: features as typeof form.platform.features };
        }
        return form.platform;
      })();
      const payload: Partial<ClientConfigData> = {
        platform: platformForSave,
        payment: form.payment,
        kyc: form.kyc,
        branding: form.branding,
      };
      const updated = await updateMyClientConfig(payload);
      hydrateForm(updated);
      toast.success("All settings saved.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      SECTION_ORDER.forEach((section) => {
        setIsSaving((prev) => ({ ...prev, [section]: false }));
      });
    }
  };

  const onValidateSection = async (section: ConfigSection) => {
    if (section === "branding") {
      toast.success("Branding saved — no validation required.");
      return;
    }
    setIsValidating((prev) => ({ ...prev, [section]: true }));
    try {
      const result = await validateClientConfigSection(section);
      if (result.validation.status === "verified") {
        toast.success(`${section.toUpperCase()} verified.`);
      } else {
        toast.error(result.validation.issues[0] || `${section.toUpperCase()} validation failed.`);
      }
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsValidating((prev) => ({ ...prev, [section]: false }));
    }
  };

  const refreshWebhookStatus = useCallback(async () => {
    try {
      const info = await getTelegramWebhookInfo();
      setWebhookConnected(info.connected);
    } catch {
      setWebhookConnected(false);
    }
  }, []);

  const onRotateSecret = async (path: string, value: string) => {
    if (!value) {
      toast.error("Enter a secret value first.");
      return;
    }

    if (path === "platform.telegram.botToken" && !/^\d+:[A-Za-z0-9_-]+$/.test(value)) {
      toast.error("Telegram Bot Token format is invalid. It should look like 123456789:ABCDEFghijklmnop (found in BotFather).");
      return;
    }
    if (path === "platform.slack.botToken" && !value.startsWith("xoxb-")) {
      toast.error("Slack Bot Token must start with xoxb-. Copy it from your Slack App → OAuth & Permissions → Bot User OAuth Token.");
      return;
    }
    if (path === "payment.razorpay.webhookSecret" && /^rzp_/i.test(value)) {
      toast.error("Razorpay Webhook Secret looks like a Key ID. Enter the webhook secret from Razorpay Dashboard → Webhooks.");
      return;
    }
    if (path === "payment.razorpay.keySecretProd" && /^rzp_/i.test(value)) {
      toast.error("Live Key Secret looks like a Key ID. Enter the actual API secret from Razorpay Dashboard → Settings → API Keys.");
      return;
    }
    if (path === "kyc.digio.clientSecret") {
      const clientId = (form.kyc?.digio?.clientId || "").trim();
      if (clientId && value.trim() === clientId) {
        toast.error("Digio Client Secret appears to be the same as the Client ID. Enter the actual secret from Digio Dashboard → API Credentials.");
        return;
      }
    }

    try {
      // Co-save adjacent plain-text fields alongside their paired secret.
      if (path === "platform.telegram.botToken") {
        const botUsername = (form.platform?.telegram?.botUsername || "").trim();
        if (botUsername) {
          const patch: Partial<ClientConfigData> = {
            platform: { telegram: { botToken: "", botUsername } },
          };
          await updateMyClientConfig(JSON.parse(JSON.stringify(patch)));
        }
      }
      if (path === "payment.razorpay.keySecretProd") {
        const keyIdProd = (form.payment?.razorpay?.keyIdProd || "").trim();
        if (keyIdProd) {
          const patch: Partial<ClientConfigData> = { payment: { razorpay: { keyIdProd } } };
          await updateMyClientConfig(JSON.parse(JSON.stringify(patch)));
        }
      }
      if (path === "kyc.digio.clientSecret") {
        const clientId = (form.kyc?.digio?.clientId || "").trim();
        if (clientId) {
          const patch: Partial<ClientConfigData> = { kyc: { digio: { clientId } } };
          await updateMyClientConfig(JSON.parse(JSON.stringify(patch)));
        }
      }
      const updated = await rotateClientConfigSecret(path, value);
      hydrateForm(updated);
      toast.success("Secret saved.");
      if (path === "platform.telegram.botToken") {
        setWebhookConnected(null);
        await refreshWebhookStatus();
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const onSyncTelegramWebhook = async () => {
    if (
      !(form.platform?.telegram?.botToken || "").trim() &&
      !secretPresence["platform.telegram.botToken"]
    ) {
      toast.error("Configure Telegram bot token in Client Settings first.");
      return;
    }

    // Auto-save only telegram token/username — never send features (SuperAdmin-only)
    try {
      const telegramPatch: Partial<ClientConfigData> = {
        platform: {
          telegram: {
            botToken: form.platform?.telegram?.botToken || "",
            botUsername: form.platform?.telegram?.botUsername || "",
          },
        },
      };
      const updated = await updateMyClientConfig(JSON.parse(JSON.stringify(telegramPatch)));
      hydrateForm(updated);
    } catch (err) {
      toast.error(`Save failed: ${getErrorMessage(err)}`);
      return;
    }

    setIsSyncingTelegramWebhook(true);
    try {
      const result = await syncTelegramWebhookFromClientConfig();
      const resolvedUsername = result.botUsername || form.platform?.telegram?.botUsername || "";
      // Persist the username confirmed by Telegram back to DB
      const telegramPatch: Partial<ClientConfigData> = {
        platform: {
          telegram: {
            botToken: "", // empty = no-op for secret fields on backend
            botUsername: resolvedUsername,
          },
        },
      };
      await updateMyClientConfig(JSON.parse(JSON.stringify(telegramPatch)));
      if (resolvedUsername) updateByPath("platform.telegram.botUsername", resolvedUsername);
      const username = resolvedUsername ? `@${resolvedUsername}` : "bot";
      toast.success(`Telegram webhook registered for ${username}.`);
      setWebhookConnected(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSyncingTelegramWebhook(false);
    }
  };

  const toggleSecretReveal = (path: string) => {
    setSecretReveal((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const secretHint = (path: string) =>
    secretPresence[path] ? "Token saved. Enter a new value to update." : "Not saved yet.";

  const copyText = async (value: string) => {
    if (!value) {
      toast.error("No value to copy.");
      return;
    }

    await navigator.clipboard.writeText(value);
    toast.success("Copied.");
  };

  if (isLoading) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle
          title="Client Settings"
          subtitle="Configure tenant-specific integration and environment values"
        />
        <div className="flex justify-center items-center py-10">
          <LoadingIndicator size="lg" text="Loading client settings..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle
          title="Client Settings"
          subtitle="Configure tenant-specific integration and environment values"
        />
        <ErrorMessage title="Failed to load settings" message={error} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <PageTitle
        title="Client Settings"
        subtitle="Client-required env settings for integrations and payments"
      />

      <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <p className="text-text-primary font-semibold">
            Onboarding Status:{" "}
            <span className={localCompletion === 100 ? "text-functional-success" : "text-golden-accent"}>
              {record?.onboarding_status || "pending"}
            </span>
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 rounded-full bg-dark-border overflow-hidden w-40">
              <div
                className={`h-full rounded-full transition-all duration-500 ${completionColor}`}
                style={{ width: completionText }}
              />
            </div>
            <span className="text-text-secondary text-sm">{completionText}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={onSaveAll} isLoading={Object.values(isSaving).some(Boolean)}>
            Save All
          </Button>
          {isDirty && <span className="text-xs text-functional-warning">Unsaved changes</span>}
        </div>
      </div>

      <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-2 sm:p-3 flex flex-wrap gap-2">
        {([
          { key: "platform", label: "Integrations" },
          { key: "payment", label: "Payments" },
          ...(kycEnabled ? [{ key: "kyc", label: "KYC" }] : []),
          { key: "branding", label: "Branding" },
        ] as Array<{ key: ConfigSection; label: string }>).map((tab) => (
          <button
            key={tab.key}
            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
              activeTab === (tab.key as ConfigSection)
                ? "bg-golden-accent text-text-on-accent"
                : "bg-dark-tertiary text-text-secondary hover:text-text-primary"
            }`}
            onClick={() => {
              if (isDirty && !window.confirm("You have unsaved changes. Switch tab anyway?")) {
                return;
              }
              setActiveTab(tab.key);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>



      {activeTab === "platform" && (<>
        <SectionCard title="Integrations" description="Required Telegram, Discord and Slack values">

          {/* ── Telegram ──────────────────────────────────────────── */}
          <div className="mb-6 rounded-xl border border-dark-border/60 bg-dark-primary/40 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border/40 bg-dark-secondary/40">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-functional-info/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-functional-info">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.34 13.772l-2.93-.916c-.637-.203-.65-.637.136-.943l11.44-4.41c.532-.194.998.13.908.718z"/>
                  </svg>
                </div>
                <span className="text-sm font-semibold text-text-primary">Telegram Setup</span>
              </div>
              {/* Requirement checklist badges */}
              <div className="flex items-center gap-2 text-xs">
                <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                  profile?.telegram_username || profile?.telegramIdLinked
                    ? "bg-functional-success/10 text-functional-success"
                    : "bg-dark-tertiary text-text-secondary"
                }`}>
                  <span>{profile?.telegram_username || profile?.telegramIdLinked ? "✓" : "○"}</span> Admin
                </span>
                <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                  form.platform?.telegram?.botUsername || secretPresence["platform.telegram.botUsername"]
                    ? "bg-functional-success/10 text-functional-success"
                    : "bg-dark-tertiary text-text-secondary"
                }`}>
                  <span>{form.platform?.telegram?.botUsername ? "✓" : "○"}</span> Bot Username
                </span>
                <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                  secretPresence["platform.telegram.botToken"]
                    ? "bg-functional-success/10 text-functional-success"
                    : "bg-dark-tertiary text-text-secondary"
                }`}>
                  <span>{secretPresence["platform.telegram.botToken"] ? "✓" : "○"}</span> Bot Token
                </span>
              </div>
            </div>

            <div className="p-4 space-y-5">
              {/* 1 — Admin personal account */}
              <div>
                <p className="text-xs font-semibold text-golden-accent uppercase tracking-wide mb-2">1. Admin Account</p>
                {profile ? (
                  <TelegramLinkForm
                    currentUsername={profile.telegram_username || null}
                    onSubmit={handleLinkTelegram}
                    onUnlink={handleUnlinkTelegram}
                    isLoading={isTelegramLoading}
                    error={telegramError}
                    profile={profile}
                  />
                ) : (
                  <p className="text-xs text-text-secondary">Loading profile...</p>
                )}
              </div>

              {/* 2 — Bot Username */}
              <div>
                <p className="text-xs font-semibold text-golden-accent uppercase tracking-wide mb-2">2. Bot Username</p>
                <Input
                  label="Bot Username (without @)"
                  name="botUsername"
                  placeholder="e.g. MyAwesomeBot"
                  value={form.platform?.telegram?.botUsername || ""}
                  onChange={(e) => updateByPath("platform.telegram.botUsername", e.target.value)}
                />
              </div>

              {/* 3 — Bot Token */}
              <div>
                <p className="text-xs font-semibold text-golden-accent uppercase tracking-wide mb-2">3. Bot Token</p>
                <SecretField
                  label="Bot Token"
                  value={form.platform?.telegram?.botToken || ""}
                  onChange={(v) => updateByPath("platform.telegram.botToken", v)}
                  maskedHint={secretHint("platform.telegram.botToken")}
                  revealed={!!secretReveal["platform.telegram.botToken"]}
                  onToggleReveal={() => toggleSecretReveal("platform.telegram.botToken")}
                  onCopy={() => copyText(form.platform?.telegram?.botToken || "")}
                  onRotate={() => onRotateSecret("platform.telegram.botToken", form.platform?.telegram?.botToken || "")}
                  error={
                    (() => {
                      const v = form.platform?.telegram?.botToken || "";
                      return v && !/^\d+:[A-Za-z0-9_-]+$/.test(v)
                        ? "Invalid format. Bot Token must look like 123456789:ABCDEFghijklmnop (get it from BotFather)."
                        : null;
                    })()
                  }
                />
              </div>

              {/* Webhook connect + endpoint */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center pt-2 border-t border-dark-border/30">
                {webhookConnected === null ? (
                  <span className="text-xs text-text-secondary">Checking webhook status...</span>
                ) : webhookConnected ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-functional-success">
                    <span className="w-2 h-2 rounded-full bg-functional-success inline-block" />
                    Webhook Connected
                  </span>
                ) : (
                  <>
                    <Button onClick={onSyncTelegramWebhook} isLoading={isSyncingTelegramWebhook}>
                      Connect Telegram Webhook
                    </Button>
                    <p className="text-xs text-text-secondary">
                      Save Bot Token first, then connect.
                    </p>
                  </>
                )}
              </div>

              {record && (form.app?.publicApiUrl || "").trim() && (
                <div className="rounded-lg border border-dark-border/60 bg-dark-primary/60 p-3">
                  <p className="text-xs font-medium text-text-secondary mb-1">Webhook Endpoint (read-only)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-text-primary break-all flex-1">
                      {form.app?.publicApiUrl || ""}
                    </code>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => copyText(form.app?.publicApiUrl || "")}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <h3 className="text-text-primary font-semibold mb-3 mt-6 flex items-center justify-between">
            Discord
            <label className={`flex items-center gap-2 select-none ${isSuperAdmin ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
              <span className="text-xs text-text-secondary font-normal">
                {form.platform?.features?.discordEnabled !== false ? "Enabled" : "Disabled"}
              </span>
              <input
                type="checkbox"
                className="w-4 h-4 accent-golden-accent"
                checked={form.platform?.features?.discordEnabled !== false}
                disabled={!isSuperAdmin}
                onChange={(e) => isSuperAdmin && updateByPath("platform.features.discordEnabled", String(e.target.checked))}
              />
            </label>
          </h3>

          {form.platform?.features?.discordEnabled !== false ? (
            <>
              {/* Central bot — just show invite URL + guild ID + webhook */}
              <div className="mb-4 rounded-lg border border-dark-border/60 bg-dark-primary/40 p-4">
                <p className="text-xs font-medium text-text-secondary mb-1">Discord uses a platform-managed central bot.</p>
                <p className="text-xs text-text-secondary mb-3">Add the bot to your server using the invite link below, then create channels via the Add Channel wizard.</p>
                {(record as any)?.config?.platform?.discord?.botInviteUrl ? (
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-text-primary break-all flex-1">
                      {(record as any).config.platform.discord.botInviteUrl}
                    </code>
                    <a
                      href={(record as any).config.platform.discord.botInviteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 px-3 py-1.5 rounded-lg border border-golden-accent/50 text-xs text-golden-accent hover:bg-golden-accent/10 transition-colors"
                    >
                      Add to Server
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-functional-warning">Discord bot invite URL not configured on the platform (DISCORD_BOT_INVITE_URL).</p>
                )}
              </div>

              <Input
                label="Guild ID (auto-linked when bot joins your server)"
                name="discord_guild_id"
                value={(record as any)?.config?.platform?.discord?.guildId || ""}
                onChange={() => {}}
                disabled
                placeholder="Auto-populated when the bot joins your Discord server"
              />

              {/* Discord webhook endpoint info */}
              {(form.app?.publicApiUrl || "").trim() && (
                <div className="mb-4 rounded-lg border border-dark-border/60 bg-dark-primary/40 p-3">
                  <p className="text-xs font-medium text-text-secondary mb-1">Discord Interactions Endpoint (read-only)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-text-primary break-all flex-1">
                      {`${(form.app?.publicApiUrl || "").replace(/\/$/, "")}/api/v1/webhooks/discord`}
                    </code>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => copyText(`${(form.app?.publicApiUrl || "").replace(/\/$/, "")}/api/v1/webhooks/discord`)}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-text-secondary mt-1">Paste this URL in your Discord Developer Portal → Application → Interactions Endpoint URL.</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-text-secondary mb-4">Discord channels are disabled. Enable to configure.</p>
          )}

          <h3 className="text-text-primary font-semibold mb-3 mt-6 flex items-center justify-between">
            Slack
            <label className={`flex items-center gap-2 select-none ${isSuperAdmin ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
              <span className="text-xs text-text-secondary font-normal">
                {form.platform?.features?.slackEnabled !== false ? "Enabled" : "Disabled"}
              </span>
              <input
                type="checkbox"
                className="w-4 h-4 accent-golden-accent"
                checked={form.platform?.features?.slackEnabled !== false}
                disabled={!isSuperAdmin}
                onChange={(e) => isSuperAdmin && updateByPath("platform.features.slackEnabled", String(e.target.checked))}
              />
            </label>
          </h3>

          {form.platform?.features?.slackEnabled !== false ? (
            <>
              <Input
                label="App Install URL"
                name="slack_app_install_url"
                value={form.platform?.slack?.appInstallUrl || ""}
                onChange={(e) => updateByPath("platform.slack.appInstallUrl", e.target.value)}
              />
              <Input
                label="Workspace URL"
                name="slack_workspace_url"
                value={form.platform?.slack?.workspaceUrl || ""}
                onChange={(e) => updateByPath("platform.slack.workspaceUrl", e.target.value)}
              />
              <SecretField
                label="Bot Token"
                value={form.platform?.slack?.botToken || ""}
                onChange={(v) => updateByPath("platform.slack.botToken", v)}
                maskedHint={secretHint("platform.slack.botToken")}
                revealed={!!secretReveal["platform.slack.botToken"]}
                onToggleReveal={() => toggleSecretReveal("platform.slack.botToken")}
                onCopy={() => copyText(form.platform?.slack?.botToken || "")}
                onRotate={() => onRotateSecret("platform.slack.botToken", form.platform?.slack?.botToken || "")}
                error={
                  (() => {
                    const v = form.platform?.slack?.botToken || "";
                    return v && !v.startsWith("xoxb-")
                      ? "Slack Bot Token must start with xoxb-. Found in Slack App → OAuth & Permissions → Bot User OAuth Token."
                      : null;
                  })()
                }
              />

              {/* Slack event subscription endpoint info */}
              {(form.app?.publicApiUrl || "").trim() && (
                <div className="mb-4 rounded-lg border border-dark-border/60 bg-dark-primary/40 p-3">
                  <p className="text-xs font-medium text-text-secondary mb-1">Slack Events Endpoint (read-only)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-text-primary break-all flex-1">
                      {`${(form.app?.publicApiUrl || "").replace(/\/$/, "")}/api/v1/webhooks/slack`}
                    </code>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => copyText(`${(form.app?.publicApiUrl || "").replace(/\/$/, "")}/api/v1/webhooks/slack`)}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-text-secondary mt-1">Paste this URL in your Slack App → Event Subscriptions → Request URL.</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-text-secondary mb-4">Slack channels are disabled. Enable to configure.</p>
          )}

          <SectionActions
            onSave={() => onSaveSection("platform")}
            onValidate={() => onValidateSection("platform")}
            saving={isSaving.platform}
            validating={isValidating.platform}
          />
        </SectionCard>
      </>)}

      {activeTab === "payment" && (
        <SectionCard title="Payments" description="Live Razorpay keys and optional webhook secret.">
          <SecretField
            label="Webhook Secret"
            value={form.payment?.razorpay?.webhookSecret || ""}
            onChange={(v) => updateByPath("payment.razorpay.webhookSecret", v)}
            maskedHint={secretHint("payment.razorpay.webhookSecret")}
            revealed={!!secretReveal["payment.razorpay.webhookSecret"]}
            onToggleReveal={() => toggleSecretReveal("payment.razorpay.webhookSecret")}
            onCopy={() => copyText(form.payment?.razorpay?.webhookSecret || "")}
            onRotate={() =>
              onRotateSecret("payment.razorpay.webhookSecret", form.payment?.razorpay?.webhookSecret || "")
            }
            error={
              /^rzp_/i.test(form.payment?.razorpay?.webhookSecret || "")
                ? "This looks like a Key ID. Webhook Secret is found in Razorpay Dashboard → Webhooks, not API Keys."
                : null
            }
          />
          <Input
            label="Live Key ID"
            name="rzp_key_id_prod"
            value={form.payment?.razorpay?.keyIdProd || ""}
            onChange={(e) => updateByPath("payment.razorpay.keyIdProd", e.target.value)}
            onBlur={async (e) => {
              const v = e.target.value.trim();
              if (v) await updateMyClientConfig({ payment: { razorpay: { keyIdProd: v } } }).catch(() => {});
            }}
          />
          <SecretField
            label="Live Key Secret"
            value={form.payment?.razorpay?.keySecretProd || ""}
            onChange={(v) => updateByPath("payment.razorpay.keySecretProd", v)}
            maskedHint={secretHint("payment.razorpay.keySecretProd")}
            revealed={!!secretReveal["payment.razorpay.keySecretProd"]}
            onToggleReveal={() => toggleSecretReveal("payment.razorpay.keySecretProd")}
            onCopy={() => copyText(form.payment?.razorpay?.keySecretProd || "")}
            onRotate={() =>
              onRotateSecret("payment.razorpay.keySecretProd", form.payment?.razorpay?.keySecretProd || "")
            }
            error={
              /^rzp_/i.test(form.payment?.razorpay?.keySecretProd || "")
                ? "This looks like a Key ID, not a secret. The API Secret is a random alphanumeric string found in Razorpay Dashboard → Settings → API Keys."
                : null
            }
          />
          <SectionActions
            onSave={() => onSaveSection("payment")}
            onValidate={() => onValidateSection("payment")}
            saving={isSaving.payment}
            validating={isValidating.payment}
          />
        </SectionCard>
      )}

      {activeTab === "kyc" && (
        <SectionCard title="KYC" description="Digio production credentials.">
          {!form.compliance?.kycEnabled ? (
            <p className="text-text-secondary text-sm py-2">
              KYC is not enabled for this account. Contact your SuperAdmin to enable it.
            </p>
          ) : (
            <>
              <Input
                label="Live Client ID"
                name="digio_client_id"
                value={form.kyc?.digio?.clientId || ""}
                onChange={(e) => updateByPath("kyc.digio.clientId", e.target.value)}
                onBlur={async (e) => {
                  const v = e.target.value.trim();
                  if (v) await updateMyClientConfig({ kyc: { digio: { clientId: v } } }).catch(() => {});
                }}
              />
              <SecretField
                label="Live Client Secret"
                value={form.kyc?.digio?.clientSecret || ""}
                onChange={(v) => updateByPath("kyc.digio.clientSecret", v)}
                maskedHint={secretHint("kyc.digio.clientSecret")}
                revealed={!!secretReveal["kyc.digio.clientSecret"]}
                onToggleReveal={() => toggleSecretReveal("kyc.digio.clientSecret")}
                onCopy={() => copyText(form.kyc?.digio?.clientSecret || "")}
                onRotate={() =>
                  onRotateSecret("kyc.digio.clientSecret", form.kyc?.digio?.clientSecret || "")
                }
                error={
                  (() => {
                    const id = (form.kyc?.digio?.clientId || "").trim();
                    const secret = (form.kyc?.digio?.clientSecret || "").trim();
                    return id && secret && secret === id
                      ? "Client Secret appears to be the same as the Client ID. Enter the actual secret from Digio Dashboard → API Credentials."
                      : null;
                  })()
                }
              />
              <SectionActions
                onSave={() => onSaveSection("kyc")}
                onValidate={() => onValidateSection("kyc")}
                saving={isSaving.kyc}
                validating={isValidating.kyc}
              />
            </>
          )}
        </SectionCard>
      )}

      {activeTab === "branding" && (
        <SectionCard title="Branding" description="Displayed on public subscription pages for your channels and combos.">
          <div className="space-y-4">
            <Input
              label="Brand Name"
              name="branding_name"
              placeholder="e.g. Livelong Wealth"
              value={form.branding?.name || ""}
              onChange={(e) => updateByPath("branding.name", e.target.value)}
            />
            {/* Logo upload */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Logo</label>
              <div className="flex items-center gap-3">
                {form.branding?.logoUrl && (
                  <img src={form.branding.logoUrl} alt="logo" className="w-10 h-10 rounded object-contain border border-dark-border/40 bg-dark-primary/40 p-1" />
                )}
                <label className="cursor-pointer px-4 py-2 rounded-lg bg-dark-tertiary border border-dark-border/50 text-sm text-text-secondary hover:text-text-primary transition-colors">
                  {form.branding?.logoUrl ? "Replace" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const result = await uploadBrandingLogo(file);
                        updateByPath("branding.logoUrl", result.logoUrl);
                        toast.success("Logo uploaded.");
                      } catch (err) {
                        toast.error(getErrorMessage(err));
                      }
                    }}
                  />
                </label>
                {form.branding?.logoUrl && (
                  <button
                    className="text-xs text-functional-danger hover:underline"
                    onClick={() => updateByPath("branding.logoUrl", "")}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-text-muted mt-1">Or paste a URL below (JPEG, PNG, WebP, SVG — max 2 MB)</p>
              <Input
                name="branding_logoUrl"
                placeholder="https://example.com/logo.png"
                value={form.branding?.logoUrl || ""}
                onChange={(e) => updateByPath("branding.logoUrl", e.target.value)}
              />
            </div>
            <Input
              label="Tagline"
              name="branding_tagline"
              placeholder="e.g. Your trusted investment partner"
              value={form.branding?.tagline || ""}
              onChange={(e) => updateByPath("branding.tagline", e.target.value)}
            />
            {(form.branding?.name || form.branding?.logoUrl) && (
              <div className="mt-2 flex items-center gap-2 p-3 rounded-lg border border-dark-border/40 bg-dark-primary/40">
                {form.branding.logoUrl && (
                  <img src={form.branding.logoUrl} alt="logo preview" className="w-7 h-7 rounded object-contain" />
                )}
                <span className="text-sm font-medium text-text-primary">{form.branding.name}</span>
                {form.branding.tagline && (
                  <span className="text-xs text-text-secondary">— {form.branding.tagline}</span>
                )}
              </div>
            )}
            <SectionActions
              onSave={() => onSaveSection("branding")}
              onValidate={() => onValidateSection("branding")}
              saving={isSaving.branding}
              validating={isValidating.branding}
            />
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
      <p className="text-text-secondary text-sm mt-1 mb-4">{description}</p>
      {children}
    </div>
  );
}

function SectionActions({
  onSave,
  onValidate,
  saving,
  validating,
}: {
  onSave: () => void;
  onValidate: () => void;
  saving: boolean;
  validating: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-3 pt-2">
      <Button onClick={onSave} isLoading={saving} disabled={validating}>
        Save Section
      </Button>
      <Button
        variant="secondary"
        onClick={onValidate}
        isLoading={validating}
        disabled={saving}
      >
        Validate Section
      </Button>
    </div>
  );
}

function SecretField({
  label,
  value,
  onChange,
  maskedHint,
  revealed,
  onToggleReveal,
  onCopy,
  onRotate,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maskedHint: string;
  revealed: boolean;
  onToggleReveal: () => void;
  onCopy: () => void;
  onRotate: () => void;
  error?: string | null;
}) {
  return (
    <div className="mb-4">
      <label className="text-sm text-text-secondary block mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          type={revealed ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-2 bg-dark-tertiary border rounded text-text-primary ${
            error ? "border-functional-danger" : "border-dark-border"
          }`}
        />
        <Button variant="secondary" onClick={onToggleReveal} type="button">
          {revealed ? "Hide" : "Reveal"}
        </Button>
        <Button variant="secondary" onClick={onCopy} type="button">
          Copy
        </Button>
        <Button onClick={onRotate} type="button">
          Save / Update
        </Button>
      </div>
      {error ? (
        <p className="text-functional-danger text-xs mt-2">{error}</p>
      ) : (
        <p className="text-text-secondary text-xs mt-2">{maskedHint}</p>
      )}
    </div>
  );
}
